import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Plus, Trash2, Send, FileDown } from "lucide-react";
import SendInvoiceModal from "@/components/SendInvoiceModal";
import { DatePicker } from "@/components/ui/date-picker";

const GST_RATE = 0.10;
const fmt = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Catalog mirrors the billable items offered on the Create Job tab — standard
// accessorial charges (with their default prices), the freight components and
// the cargo item types — so a line can be added with one click, plus an
// "Other (Specify)" option for a blank manual line. Keep in sync with
// ACCESSORIAL_CHARGES / ITEM_TYPES in CreateJob.jsx.
const LINE_CATALOG = [
  { group: "Service charges", items: [
    { label: "Inbound Connect", rate: 35, kind: "accessorial" },
    { label: "Tail Lift", rate: 100, kind: "accessorial" },
    { label: "Hand Unload", rate: 55, kind: "accessorial" },
    { label: "Dangerous Goods", rate: 55, kind: "accessorial" },
  ] },
  { group: "Freight", items: [
    { label: "Freight", rate: 0, kind: "freight" },
    { label: "Fuel Levy", rate: 0, kind: "fuel" },
    { label: "Toll", rate: 0, kind: "toll" },
  ] },
  { group: "Item types", items: [
    { label: "Pallet", rate: 0, kind: "item" },
    { label: "Crate", rate: 0, kind: "item" },
    { label: "Box", rate: 0, kind: "item" },
    { label: "IBC", rate: 0, kind: "item" },
    { label: "Carton", rate: 0, kind: "item" },
  ] },
];

// Full-page invoice editor (replaces the old popup). Reached via
// /dashboard/invoicing/:invoiceId/edit. Add / delete / edit every line item,
// dates and notes; save (edits reflect in Job Status / History) or save & send.
export default function InvoiceEdit() {
  const { user } = useAuth();
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [lines, setLines] = useState([]);
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(null);
  const [showCatalog, setShowCatalog] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr("");
    (async () => {
      try {
        const { data } = await api.get(`/invoices/${invoiceId}`);
        if (cancelled) return;
        setInvoice(data);
        setLines((data.line_items || []).map((l) => ({ ...l, _key: l.id || Math.random().toString(36).slice(2) })));
        setInvoiceDate(data.invoice_date || "");
        setDueDate(data.due_date || "");
        setNotes(data.notes || "");
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [invoiceId]);

  const subtotal = useMemo(() => lines.reduce((a, l) => a + Number(l.qty || 0) * Number(l.rate || 0), 0), [lines]);
  const gst = +(subtotal * GST_RATE).toFixed(2);
  const total = +(subtotal + gst).toFixed(2);

  const setLine = (key, field, val) => setLines((ls) => ls.map((l) => (l._key === key ? { ...l, [field]: val } : l)));
  const addLine = () => setLines((ls) => [...ls, { _key: Math.random().toString(36).slice(2), kind: "custom", description: "", qty: 1, rate: 0, job_id: null, job_ref: null }]);
  const delLine = (key) => setLines((ls) => ls.filter((l) => l._key !== key));
  const addCatalogItem = (item) => {
    setLines((ls) => [...ls, {
      _key: Math.random().toString(36).slice(2), kind: item.kind || "custom",
      description: item.label, qty: 1, rate: Number(item.rate || 0), job_id: null, job_ref: null,
    }]);
    setShowCatalog(false);
  };

  const buildPayload = () => ({
    invoice_date: invoiceDate || undefined,
    due_date: dueDate || undefined,
    notes,
    line_items: lines.map((l) => ({
      id: l.id, job_id: l.job_id || null, job_ref: l.job_ref || null, kind: l.kind || "custom",
      description: l.description || "", qty: Number(l.qty || 0), rate: Number(l.rate || 0),
    })),
  });

  const save = async (thenSend = false) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/invoices/${invoiceId}`, buildPayload());
      toast.success("Invoice saved");
      if (thenSend) { setInvoice(data); setSending(data); }
      else navigate("/dashboard/invoicing");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const openPdf = async () => {
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (user && user.role !== "admin") return <Navigate to="/dashboard/job-status" replace />;

  if (loading) return <div className="flex items-center justify-center py-24 text-white/50 text-sm">Loading invoice…</div>;
  if (err) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate("/dashboard/invoicing")} className="text-white/60 hover:text-white text-sm inline-flex items-center gap-1"><ArrowLeft size={15} /> Back to Invoicing</button>
        <div className="p-6 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200">{String(err)}</div>
      </div>
    );
  }
  if (!invoice) return null;

  const isMerged = invoice.type === "merged";

  return (
    <div className="space-y-5 max-w-4xl" data-testid="invoice-edit-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button onClick={() => navigate("/dashboard/invoicing")} className="text-white/55 hover:text-white text-xs inline-flex items-center gap-1 mb-2" data-testid="back-to-invoicing">
            <ArrowLeft size={14} /> Back to Invoicing
          </button>
          <h1 className="brand-title text-2xl md:text-3xl flex items-center gap-2">
            <Pencil size={20} className="text-[#19e6c8]" /> Edit {invoice.invoice_number}
            {isMerged && <span className="text-[11px] px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-300">merged</span>}
            {invoice.edited && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300">edited</span>}
          </h1>
          <p className="text-white/55 text-sm mt-1">
            {invoice.customer_name} · edits reflect in Job Status / History{isMerged ? ` · ${(invoice.job_refs || []).length} jobs consolidated` : ""}
          </p>
        </div>
        <button onClick={openPdf} className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="preview-pdf-btn">
          <FileDown size={14} /> Preview PDF
        </button>
      </div>

      {/* Dates */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
          <div>
            <label className="block text-white/60 text-[11px] uppercase tracking-wider mb-1">Invoice Date</label>
            <DatePicker value={(invoiceDate || "").slice(0, 10)} onChange={setInvoiceDate} className="!py-2" data-testid="edit-invoice-date" />
          </div>
          <div>
            <label className="block text-white/60 text-[11px] uppercase tracking-wider mb-1">Due Date</label>
            <DatePicker value={(dueDate || "").slice(0, 10)} onChange={setDueDate} className="!py-2" data-testid="edit-due-date" />
          </div>
        </div>
      </div>

      {/* Line items */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-white/90">Line Items</h2>
          {isMerged && <span className="text-[11px] text-white/40">grouped by source job — <span className="text-pink-300 font-mono">[JOB-REF]</span> tags each line</span>}
        </div>
        <div className="rounded-xl border border-white/10 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-[11px] uppercase tracking-wider text-white/50">
              <tr>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-right w-24">Qty</th>
                <th className="px-3 py-2 text-right w-32">Rate</th>
                <th className="px-3 py-2 text-right w-32">Amount</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l._key} className="border-t border-white/5">
                  <td className="px-3 py-2">
                    {l.job_ref && <span className="text-[10px] font-mono text-pink-300 mr-1">[{l.job_ref}]</span>}
                    <input value={l.description} onChange={(e) => setLine(l._key, "description", e.target.value)}
                      className="bg-transparent border border-white/10 rounded px-2 py-1.5 text-white w-full focus:outline-none focus:border-[#19e6c8]/50" placeholder="Description" />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" step="0.01" value={l.qty} onChange={(e) => setLine(l._key, "qty", e.target.value)}
                      className="bg-transparent border border-white/10 rounded px-2 py-1.5 text-white w-full text-right focus:outline-none focus:border-[#19e6c8]/50" />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" step="0.01" value={l.rate} onChange={(e) => setLine(l._key, "rate", e.target.value)}
                      className="bg-transparent border border-white/10 rounded px-2 py-1.5 text-white w-full text-right focus:outline-none focus:border-[#19e6c8]/50" />
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-white/80">{fmt(Number(l.qty || 0) * Number(l.rate || 0))}</td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => delLine(l._key)} className="text-white/40 hover:text-red-400" title="Delete line"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
              {lines.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-white/40">No line items — add one below.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="relative inline-block mt-3">
          <button onClick={() => setShowCatalog((s) => !s)} className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 text-xs font-semibold flex items-center gap-1.5" data-testid="add-line-btn">
            <Plus size={14} /> Add line item
          </button>
          {showCatalog && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowCatalog(false)} />
              <div className="absolute left-0 z-20 mt-2 w-72 max-h-80 overflow-auto bg-[#0b1430] border border-white/15 rounded-xl shadow-2xl p-2" data-testid="line-catalog">
                {LINE_CATALOG.map((grp) => (
                  <div key={grp.group} className="mb-1">
                    <div className="text-[10px] uppercase tracking-wider text-white/40 px-2 py-1">{grp.group}</div>
                    {grp.items.map((it) => (
                      <button key={it.label} type="button" onClick={() => addCatalogItem(it)}
                        className="w-full flex items-center justify-between px-2 py-1.5 rounded-md text-sm text-white/85 hover:bg-white/10"
                        data-testid={`catalog-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        <span>{it.label}</span>
                        {it.rate > 0 && <span className="font-mono text-xs text-emerald-300">{fmt(it.rate)}</span>}
                      </button>
                    ))}
                  </div>
                ))}
                <div className="border-t border-white/10 mt-1 pt-1">
                  <button type="button" onClick={() => { addLine(); setShowCatalog(false); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-[#19e6c8] hover:bg-white/10"
                    data-testid="catalog-other">
                    <Plus size={13} /> Other (Specify) — blank manual line
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-5">
          <label className="block text-white/60 text-[11px] uppercase tracking-wider mb-1">Notes (shown on the invoice)</label>
          <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} className="df-input-rect resize-none" placeholder="Optional note for the customer" />
        </div>

        <div className="mt-5 flex justify-end">
          <div className="w-full max-w-xs space-y-1.5 text-sm">
            <div className="flex justify-between text-white/70"><span>Subtotal</span><span className="font-mono">{fmt(subtotal)}</span></div>
            <div className="flex justify-between text-white/70"><span>GST (10%)</span><span className="font-mono">{fmt(gst)}</span></div>
            <div className="flex justify-between text-white font-bold text-base border-t border-white/10 pt-1.5"><span>Total Due</span><span className="font-mono text-[#19e6c8]">{fmt(total)}</span></div>
          </div>
        </div>
      </div>

      {/* Sticky-ish action bar */}
      <div className="flex items-center justify-end gap-2 pb-2">
        <button onClick={() => navigate("/dashboard/invoicing")} className="px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white/80 text-sm font-semibold">Cancel</button>
        <button onClick={() => save(false)} disabled={busy} className="px-5 py-2.5 rounded-lg text-[#04201c] font-semibold disabled:opacity-50" style={{ background: "linear-gradient(90deg,#19e6c8,#0fbfa6)" }} data-testid="save-invoice-btn">
          {busy ? "Saving…" : "Save"}
        </button>
        <button onClick={() => save(true)} disabled={busy} className="px-5 py-2.5 rounded-lg text-white font-semibold flex items-center gap-2 disabled:opacity-50" style={{ background: "linear-gradient(90deg,#22c55e,#16a34a)" }} data-testid="save-send-invoice-btn">
          <Send size={15} /> Save &amp; Send
        </button>
      </div>

      {sending && (
        <SendInvoiceModal invoice={sending} onClose={() => setSending(null)} onSent={() => { setSending(null); navigate("/dashboard/invoicing"); }} />
      )}
    </div>
  );
}
